/**
 * Tenant Detail Page - Admin
 * View and manage individual tenant details with pricing configuration
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Settings,
  CreditCard,
  Users,
  Activity,
  Loader2,
} from 'lucide-react';
import Card from '../../components/common/Card';
import PricingSystemConfig from '../../components/settings/PricingSystemConfig';
import tenantService from '../../services/tenantService';
import { toast } from 'react-hot-toast';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'pricing', label: 'Pricing Configuration', icon: Settings },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'activity', label: 'Activity', icon: Activity },
];

export default function TenantDetail() {
  const { tenantId } = useParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch tenant info
  const { data: tenant, isLoading: tenantLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId],
    queryFn: () => tenantService.getTenant(tenantId),
  });

  // Fetch tenant pricing settings
  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'settings'],
    queryFn: () => tenantService.getTenantSettings(tenantId),
    enabled: activeTab === 'pricing',
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (updates) => tenantService.updateTenantSettings(tenantId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'settings'] });
      toast.success('Tenant settings updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update settings');
    },
  });

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tenantInfo = tenant?.tenant || tenant;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/tenants" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{tenantInfo?.name || tenantId}</h1>
          <p className="text-gray-500">{tenantInfo?.slug || tenantId}</p>
        </div>
        <span className={`ml-auto px-3 py-1 rounded-full text-sm ${
          tenantInfo?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {tenantInfo?.status || 'unknown'}
        </span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <Card>
          <div className="p-6 grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-500">Tenant ID</label>
              <p className="font-mono text-gray-800 dark:text-white">{tenantId}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Plan</label>
              <p className="text-gray-800 dark:text-white">{tenantInfo?.plan || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Billing Type</label>
              <p className="text-gray-800 dark:text-white">{tenantInfo?.billing_type || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Contact Email</label>
              <p className="text-gray-800 dark:text-white">{tenantInfo?.contact_email || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Created</label>
              <p className="text-gray-800 dark:text-white">{tenantInfo?.created_at ? new Date(tenantInfo.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'pricing' && (
        <div className="space-y-4">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-yellow-800 dark:text-yellow-200 text-sm">
              <strong>Admin Mode:</strong> Changes here will affect tenant <strong>{tenantInfo?.name || tenantId}</strong>'s pricing configuration.
            </p>
          </div>

          {settingsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <PricingSystemConfig
              systemConfig={settingsData?.settings?.system_config}
              onSave={(data) => updateSettingsMutation.mutate(data)}
              isSaving={updateSettingsMutation.isPending}
            />
          )}
        </div>
      )}

      {activeTab === 'billing' && (
        <Card>
          <div className="p-6 text-center text-gray-500">
            Billing information coming soon
          </div>
        </Card>
      )}

      {activeTab === 'users' && (
        <Card>
          <div className="p-6 text-center text-gray-500">
            User management coming soon
          </div>
        </Card>
      )}

      {activeTab === 'activity' && (
        <Card>
          <div className="p-6 text-center text-gray-500">
            Activity log coming soon
          </div>
        </Card>
      )}
    </div>
  );
}
